/**
 * diaApi — cliente de la jornada diaria expuesta por payment.
 *
 *   GET /api/dia?fecha=YYYY-MM-DD&dias=14
 *
 * Una sola llamada devuelve las tres secciones del día: lo recaudado por
 * compañía, los asientos de los cobros de ese día y su estado frente a Odoo, y las
 * cuotas que vencen en la ventana siguiente. El browser NUNCA lee
 * `finance.movements` directo (no tiene GRANT para `authenticated`): la lectura
 * administrativa sale de payment con la secret key.
 *
 * Mismo patrón que settlementApi.js / financeLedgerApi.js: JWT de staff Clerk,
 * URL vía baseUrl.js (same-origin → proxy api/yoolpay/api/[endpoint].js), y el
 * error se lanza como `Error(mensaje)` sin `err.status` — convención del repo.
 *
 * @see src/services/yoolpay/settlementApi.js — cliente hermano (patrón copiado)
 * @see src/pages/payments/yoolpay/YoolPayDia.jsx — único consumidor
 */
import { yoolpayBaseUrl } from './baseUrl.js';

const BASE = yoolpayBaseUrl();

async function call(path, token, { method = 'GET', body } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error || `payment respondió ${res.status}`);
  return payload;
}

const qs = (params) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ''),
  ).toString();

/**
 * La jornada de un día.
 *
 * `fecha` en 'YYYY-MM-DD'; omitirla abre el día de hoy según el reloj UTC del
 * servidor (el mismo corte con que el ciclo de cobro selecciona las cuotas), NO
 * el del browser. La pantalla siempre la manda explícita para que el día que se
 * ve sea el día que se pidió.
 *
 * `dias` es el horizonte de `proximos` (default 14 en payment, tope 90).
 */
export const getDia = (token, { fecha, dias } = {}) =>
  call(`/api/dia?${qs({ fecha, dias })}`, token);

/**
 * PASO 3 del cierre diario — acepta el depósito del PSP de ese día.
 *
 * EL MONTO ES EL DE LA CARTOLA, no el esperado. Si difiere, esa diferencia es un descuadre
 * que se resuelve con el proveedor: mandar el esperado lo esconde, y ya pasó — el botón del
 * mes mandaba el bruto recaudado y dejó mayo con un descuadre inventado de $988.
 *
 * EL AUTOR NO SE MANDA. Antes iba un `por` en el body y payment lo escribía tal cual en
 * `confirmado_por`: el cliente declaraba quién firmaba, así que la columna de auditoría
 * guardaba lo que le escribieran. Ahora payment lo saca del JWT de Clerk —el mismo token que
 * ya viaja en el header— y el body no puede influir en él.
 *
 * `odoo_statement_line_id` ES OBLIGATORIO desde el 2026-08-21, y es el id de la línea de
 * cartola que el propio paso 3 ya trae en su candidato. Antes no se mandaba: payment
 * escribía el monto y la fecha que le llegaran en el body, así que la verificación contra
 * Odoo existía sólo al PINTAR la pantalla y no al aceptar. Con el id, payment vuelve a leer
 * la cartola y comprueba que la línea existe y dice el mismo monto antes de afirmar que la
 * plata llegó — que es lo que habilita el giro.
 */
export const aceptarDeposito = (
  token,
  { fecha, psp, monto_clp, fecha_extracto, cartola, odoo_statement_line_id },
) =>
  call('/api/dia?accion=aceptar_deposito', token, {
    method: 'POST',
    body: { fecha, psp, monto_clp, fecha_extracto, cartola, odoo_statement_line_id },
  });

/**
 * PASO 4 del cierre diario — manda a Odoo los asientos que faltan.
 *
 * POSTEA EL ASIENTO DEL DÍA: `DE-YYMM-nnnn` con el depósito del PSP, el fee anticipado y
 * el pasivo agregado POR COMPAÑÍA. Uno por día y por PSP.
 *
 * VA ACOTADO AL DÍA, y por eso lleva `fecha`. Antes mandaba el body vacío y el paso corría
 * el outbox entero —una cola global y FIFO—, así que el botón del día 15 procesaba también
 * lo de otros días. Peor: desde que se retiró el posteo por cobro, el outbox dejó de
 * contabilizar nada, así que el botón respondía 200 y en Odoo no aparecía nada.
 *
 * Idempotente por la `ref` del asiento: apretarlo dos veces devuelve el mismo asiento.
 *
 * Responde 409 —no 500— si el día ya tiene asientos POSTEADOS en Odoo: postear encima
 * duplicaría el pasivo con la aseguradora. Es el guard funcionando, no una falla, y el
 * mensaje dice qué hacer.
 */
export const exportarAsientos = (token, { fecha, psp }) =>
  call('/api/dia?accion=exportar_asientos', token, { method: 'POST', body: { fecha, psp } });

/**
 * PASO 5 del cierre diario — crea en Odoo los pedidos de venta y compra del día.
 *
 * Uno por (compañía, entidad) con la suma de sus fees, más el de compra si el PSP retuvo
 * algo. Idempotente por `UNIQUE (fecha, compania_id, entidad)` y porque Odoo busca por
 * `client_order_ref` antes de crear: reprocesar un día no duplica el fee.
 *
 * Responde 207 si alguno falló — una compañía sin `id_odoo` no tiene a quién facturarle y
 * no debe bloquear a las demás. El detalle viene por pedido.
 */
export const crearPedidos = (token, { fecha, psp }) =>
  call('/api/dia?accion=crear_pedidos', token, {
    method: 'POST',
    body: { fecha, psp },
  });

/**
 * PASO 6 del cierre diario — registra que el giro a UNA compañía salió del banco.
 *
 * UNO POR COMPAÑÍA, a diferencia del depósito: entra una transferencia del PSP y salen N.
 *
 * `referencia` es obligatoria: es el número de la transferencia, y sin él el giro no se
 * puede cruzar contra la cartola.
 *
 * `monto_clp` LO VERIFICA PAYMENT contra lo recaudado por esa compañía ese día, y responde
 * 422 si no calza. Hay que mandar el `a_girar_clp` que vino en el GET, sin editarlo: a la
 * compañía se le gira el 100% del bruto de sus cobros, así que una cifra distinta es un
 * descuadre que se resuelve antes de registrarlo.
 *
 * REGISTRAR ALGO DISTINTO SOBRE UN GIRO YA REGISTRADO TAMBIÉN DA 422. La transferencia ya
 * salió del banco; la fila no se pisa. Repetir la misma llamada sí funciona y devuelve la
 * fila que ya estaba.
 *
 * El autor sale del JWT, igual que en `aceptarDeposito`.
 */
export const registrarGiroDelDia = (
  token,
  {
    fecha, psp, compania_id, compania_nombre, monto_clp, referencia, via, fecha_transferencia,
    // Por qué quien aceptó el depósito del día registra también el giro. payment la exige
    // —mínimo 8 caracteres— SÓLO en ese caso, y responde 422 si falta. Va vacía el resto
    // de las veces, que es lo normal: fueron dos personas.
    justificacion,
  },
) =>
  call('/api/dia?accion=registrar_giro', token, {
    method: 'POST',
    body: {
      fecha, psp, compania_id, monto_clp, referencia, via, fecha_transferencia,
      // Sólo para la GLOSA del asiento: «Giro a MAPFRE — cierra el pasivo». El monto y la
      // compañía los verifica payment por su cuenta; esto no participa de ninguna decisión.
      compania_nombre,
      justificacion,
    },
  });
